import { Component, Input, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';

import { I18nService } from '@recruitee/i18n';
import { QuestionKind } from '@recruitee/interview-templates-types';
import {
  CheckboxComponent,
  GroupComponent,
  ProtectedControlDirective,
  RadioFormGroupComponent,
  RadioGroupItem,
  SelectComponent,
  SelectOption,
} from '@recruitee/user-interface';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import { QuestionBase } from '../interview-kind';
import { InterviewResultsModule } from '../interview-results.module';

type QuestionChoiceKinds =
  | QuestionKind.Dropdown
  | QuestionKind.SingleChoice
  | QuestionKind.MultipleChoice
  | QuestionKind.Boolean;

type QuestionOptionsControl = FormControl<{ title: string }>;

interface QuestionSingleChoiceControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.SingleChoice>;
  content: FormControl<string>;
  options?: FormArray<QuestionOptionsControl>;
}
export type QuestionSingleChoiceControl = FormGroup<QuestionSingleChoiceControls>;

interface QuestionMultipleChoiceControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.MultipleChoice>;
  contents: FormArray<FormControl<string>>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionMultipleChoiceControl = FormGroup<QuestionMultipleChoiceControls>;

interface QuestionDropdownControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Dropdown>;
  content: FormControl<string>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionDropdownControl = FormGroup<QuestionDropdownControls>;

interface QuestionBooleanControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Boolean>;
  content: FormControl<string>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionBooleanControl = FormGroup<QuestionBooleanControls>;

type QuestionChoiceControl =
  | QuestionSingleChoiceControl
  | QuestionMultipleChoiceControl
  | QuestionDropdownControl
  | QuestionBooleanControl;

type IsControl<
  T extends QuestionChoiceControl,
  Part extends Record<string, AbstractControl>,
> = T extends FormGroup<infer R> ? (R extends Part ? FormGroup<R> : never) : never;

@Component({
  selector: 'rt-interview-choice',
  templateUrl: 'interview-choice.component.html',
  styleUrls: ['./interview-choice.component.less'],
  standalone: true,
  imports: [
    InterviewResultsModule,
    ReactiveFormsModule,
    RadioFormGroupComponent,
    ProtectedControlDirective,
    GroupComponent,
    CheckboxComponent,
    SelectComponent,
    InterviewBaseBoxComponent,
  ],
})
export class InterviewChoiceComponent<Control extends QuestionChoiceControl> {
  @Input() public questionForm: Control;
  @Input() public formSubmissionAttempt: boolean = false;
  @Input() public customOptions?: SelectOption[] | RadioGroupItem[];

  public options: SelectOption[] | RadioGroupItem[] = [];

  public QuestionKind: typeof QuestionKind = QuestionKind;

  constructor(private i18n: I18nService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.customOptions && this.customOptions) {
      this.options = this.customOptions;
    }

    if (!this.customOptions) {
      const value = (this.questionForm && this.questionForm.getRawValue()) || {};
      if (QuestionKind.Dropdown === value.questionKind) {
        this.options = this.prepareSelectItems(value.options);
      } else if (QuestionKind.SingleChoice === value.questionKind && value.options) {
        this.options = this.prepareRadioItems(value.options);
      } else if (QuestionKind.Boolean === value.questionKind) {
        this.options = this.getBooleanOptionList();
      }
    }
  }

  public isInvalid(questionForm: Control): boolean {
    const isContentInvalid = this.isContentControl(questionForm)
      ? questionForm.controls.content.invalid
      : false;
    const isContentsInvalid = this.isContentsControl(questionForm)
      ? questionForm.controls.contents.invalid
      : false;
    const isDirtyOrTouched = questionForm.dirty || questionForm.touched;

    return (isContentInvalid || isContentsInvalid) && isDirtyOrTouched;
  }

  public trackByOption<T extends QuestionOptionsControl>(index: number, option: T): string {
    return option.value.title;
  }

  public isQuestionType<T extends Control, K extends QuestionChoiceKinds>(
    questionForm: T,
    kind: K,
  ): questionForm is IsControl<T, { questionKind: FormControl<K> }> {
    return questionForm.getRawValue().questionKind === kind;
  }

  private getBooleanOptionList(): RadioGroupItem[] {
    return [
      {
        option: this.i18n.translate('app.interview.boolean.yes'),
        value: 'yes',
      },
      {
        option: this.i18n.translate('app.interview.boolean.no'),
        value: 'no',
      },
    ];
  }

  private prepareSelectItems(options: Array<{ title: string }>): SelectOption[] {
    return options.map(option => ({
      label: option.title,
      value: option.title,
    }));
  }

  private prepareRadioItems(options: Array<{ title: string }>): RadioGroupItem[] {
    return options.map(option => ({
      option: option.title,
      value: option.title,
    }));
  }

  private isContentControl<T extends Control>(
    control: T,
  ): control is IsControl<T, { content: FormControl<string> }> {
    return 'content' in control.controls;
  }

  private isContentsControl<T extends Control>(
    control: T,
  ): control is IsControl<T, { contents: FormArray<FormControl<string>> }> {
    return 'contents' in control.controls;
  }
}
